// OIOSAML3 failures, as they arrive back on /login — sign-in below, sign-out at
// the bottom of this file. One module because the two share the query string
// they arrive on, including the `ref` parameter.
//
// Account provisioning happens at the IdP callback now, before the browser is sent
// back here. When that server-side step fails there is nothing to exchange, so ufs
// redirects to `/login?sso_error=<code>&ref=<uuid>` instead of `/login?totp=<token>`.
// The two are mutually exclusive — a redirect carries one or the other, never both —
// so the totp branch in login.tsx is untouched by this.
//
// Before this existed the page simply ignored the redirect and rendered its idle
// login form: the user came back from a *successful* IdP sign-in to a blank form
// with no indication that anything had gone wrong, and no reason to suspect that
// clicking Employee Login again would fail the same way.
//
// ufs deliberately sends only these opaque codes and never any human-readable text,
// so every word the user reads is defined in this repo's i18n bundles.

export const SSO_ERROR_PARAM = 'sso_error';

// The correlation id, spelled `ref` by BOTH redirects — the sign-in failure and
// the sign-out failure at the bottom of this file. Sharing it is safe because the
// error VIEW is gated on `sso_error` alone and reads `ref` only once that gate is
// open, so a logout report can never light up the login-failure page.
export const ERROR_REF_PARAM = 'ref';

const SSO_ERROR_CODES = [
  'assertion_invalid',
  'role_unmapped',
  'provision_failed',
] as const;

type SsoErrorCode = typeof SSO_ERROR_CODES[number];

// Explicit literals rather than `auth_sso_error_${code}` so every key the app can
// render stays greppable from the bundles, and `Record<SsoErrorCode, string>` makes
// a code added to the tuple without a message a tsc error rather than a live
// fallback to the generic text.
const SSO_ERROR_MESSAGE_KEY: Record<SsoErrorCode, string> = {
  assertion_invalid: 'auth_sso_error_assertion_invalid',
  role_unmapped: 'auth_sso_error_role_unmapped',
  provision_failed: 'auth_sso_error_provision_failed',
};

export const SSO_ERROR_GENERIC_KEY = 'auth_sso_error_generic';

// Membership via the tuple, NOT `code in SSO_ERROR_MESSAGE_KEY` / a bare index: the
// code is untrusted URL input, and an inherited key (`?sso_error=constructor`,
// `toString`, …) indexes to a *function* on a plain object literal. That would be
// truthy, so an `|| GENERIC` guard would pass it straight through to lt() and React.
const isSsoErrorCode = (code: string): code is SsoErrorCode =>
  (SSO_ERROR_CODES as readonly string[]).includes(code);

/**
 * i18n key for the message to show for a `sso_error` code off the query string.
 *
 * Anything outside the three frozen codes — a typo, a hand-edited URL, a code from
 * a newer ufs than this build knows — resolves to the generic failure message. The
 * raw parameter value is never returned, so it can never reach the DOM.
 */
export const ssoErrorMessageKey = (code: string | null | undefined): string =>
  code && isSsoErrorCode(code)
    ? SSO_ERROR_MESSAGE_KEY[code]
    : SSO_ERROR_GENERIC_KEY;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The correlation id, or null when there is nothing trustworthy to carry.
 *
 * `ref` is the one value here that leaves this module VERBATIM — into the DOM on
 * the sign-in error page, into the console on a sign-out failure. On an
 * unauthenticated page that makes it the obvious place to plant text which reads
 * as if the product wrote it (`?sso_error=x&ref=Ring+70+00+00+00+for+at+få+adgang`).
 * The contract says uuid, so anything that is not one is dropped: the reference
 * line does not render at all — the message above it still does, which is the part
 * the user needs — and the log line simply carries no ref.
 */
export const correlationRef = (ref: string | null | undefined): string | null =>
  ref && UUID_PATTERN.test(ref) ? ref : null;

// ───────────────────── SP-initiated single logout (SLO) ─────────────────────
//
// The OTHER direction, landing on the same page. Sign-out starts as a form POST to
// ufs (components/sidebar/sidebar.tsx) and comes back as a redirect:
//
//   {tenantHomePage}/login                                 · the IdP session ended
//   {tenantHomePage}/login?slo_error=<code>&ref=<uuid>     · it did not
//
// ufs used to redirect to the bare success URL either way. It now reports the
// failure, and today it reports it EVERY time: the IdP answers each LogoutRequest
// with `urn:oasis:names:tc:SAML:2.0:status:Requester` and the session survives.
//
// ⚠️ DEVELOPER-VISIBLE ONLY, ON PURPOSE. There is no message, no error view and no
// change to what the user sees — the idle login form comes back exactly as it does
// after a clean logout. Since SLO currently fails for everyone, a user-facing notice
// would mean every SSO user reading an error on every single logout; whether that is
// the right thing to show them is a PRODUCT decision and is not this change's to
// make. What this code fixes is narrower and not a judgement call: the server had
// started saying "the logout did not happen" and the browser was throwing the
// sentence away.
export const SLO_ERROR_PARAM = 'slo_error';

// Shape, not membership — the opposite of the choice made for `sso_error` above,
// and for the opposite reason. An `sso_error` code SELECTS the user's copy, so one
// this build does not know must collapse to the generic message. A `slo_error` code
// selects nothing; it is diagnostic text, and a code from a newer ufs than this
// build knows is exactly what an engineer reading the console needs to see. So the
// value survives whenever it LOOKS like a code, and only its shape is enforced —
// which is enough that a hand-edited URL cannot write sentences, newlines or
// terminal escapes into a log line.
const SLO_ERROR_CODE_PATTERN = /^[a-z0-9_]{1,64}$/;

// The marker a value that is not code-shaped collapses to. Deliberately a word
// no ufs code could be, so a log line can never be ambiguous about which it is.
const SLO_ERROR_CODE_UNRECOGNISED = 'unrecognised';

/**
 * The `slo_error` code, in a shape that is safe to put in a log line.
 *
 * `idp_rejected` is the only code ufs sends today; anything shaped like a code is
 * passed through unchanged, and anything else — free text, an empty value, a
 * multi-line payload — becomes the fixed `unrecognised` marker. The raw value is
 * never returned, so it can reach neither the console nor the DOM.
 */
export const sloErrorCodeForLog = (code: string | null | undefined): string =>
  code && SLO_ERROR_CODE_PATTERN.test(code)
    ? code
    : SLO_ERROR_CODE_UNRECOGNISED;
